import { memo, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  ScrollArea,
} from '@flote/ui';
import { VehicleForm, type VehicleFormData } from './VehicleForm';
import type { Team, Vehicle } from '@flote/contracts/http';

interface VehicleFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: VehicleFormData) => Promise<void>;
  isProcessing?: boolean;
  teams: Team[];
  vehicle?: Vehicle;
}

export const VehicleFormModal = memo<VehicleFormModalProps>(({
  open,
  onOpenChange,
  onSubmit,
  isProcessing = false,
  teams,
  vehicle,
}) => {
  const isEditMode = !!vehicle;

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen && !isProcessing) {
        onOpenChange(false);
      }
    },
    [onOpenChange, isProcessing]
  );

  const initialData = vehicle
    ? {
        type: vehicle.type,
        description: vehicle.description,
        licensePlate: vehicle.licensePlate,
        color: vehicle.color,
        teamId: vehicle.teamId,
        capacity: vehicle.capacity,
        notes: vehicle.notes,
        status: vehicle.status,
      }
    : undefined;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="w-[calc(100%-2rem)] max-w-lg max-h-[90vh] p-0 sm:w-full">
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle>{isEditMode ? 'Edit Vehicle' : 'Add Vehicle'}</DialogTitle>
          <DialogDescription>
            {isEditMode
              ? 'Update vehicle information and settings'
              : 'Add a new vehicle to your fleet'}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[calc(90vh-120px)]">
          <div className="px-6 pb-6">
            <VehicleForm
              onSubmit={onSubmit}
              isProcessing={isProcessing}
              teams={teams}
              initialData={initialData}
              mode={isEditMode ? 'edit' : 'create'}
            />
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
});

VehicleFormModal.displayName = 'VehicleFormModal';
